import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  Scissors, 
  Activity, 
  Stethoscope, 
  Scan, 
  ShieldCheck, 
  Microscope, 
  Layers, 
  AlertCircle 
} from "lucide-react";
import image1 from '../../Assets/Services/Service6-1.jpg'
import image2 from '../../Assets/Services/Service6-2.jpg'
import { Helmet } from "react-helmet";

const OralSurgery = () => {
  return (
    <ServiceLayout
      title="Oral & Maxillofacial Surgery"
      subtitle="Expert surgical care to restore comfort, function, and confidence."
      heroImage="/images/hero/oral-surgery.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <Helmet>
  <title>Oral Surgery Vijayawada | Tooth Removal</title>
  <meta name="description" content="Expert oral surgery in Vijayawada including tooth extraction, wisdom tooth removal and minor dental procedures." />
  <meta name="keywords" content="oral surgery Vijayawada, tooth extraction" />
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sridhardental.com/service/oral-surgery" />
</Helmet>
        <p className="text-lg text-gray-700 leading-relaxed">
          Oral and Maxillofacial Surgery is a specialized field of dentistry that focuses on diagnosing and
surgically treating conditions affecting the teeth, jaws, mouth, and facial structures. These
procedures require advanced surgical expertise, modern technology, and precise treatment
planning to ensure safe and effective results.
        </p>
        <p className="mt-4 text-gray-700">
          At Dr. Sridhar International Dental Hospitals, our experienced oral and maxillofacial surgeons
provide advanced surgical care in Vijayawada and Hyderabad using modern diagnostic imaging and
state-of-the-art facilities to deliver precise, comfortable, and reliable treatment outcomes.
        </p>
      </section>

      {/* Scope Section */}
      <section className="bg-blue-50 p-8 rounded-3xl mb-12 border border-blue-100">
        <h2 className="text-2xl font-bold text-blue-900 mb-6 flex items-center gap-2">
          <Stethoscope className="text-blue-600" /> What is Oral & Maxillofacial Surgery?
        </h2>
        <p>Oral and Maxillofacial Surgery is a specialized field of dentistry that focuses on diagnosing and
surgically treating conditions affecting the teeth, jaws, mouth, and facial structures.</p>
        <p className="text-gray-700 mb-6">Surgical procedures related to:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["Teeth & Gums", "Jaw Bones", "Facial Bones", "Oral Tissues"].map((item, idx) => (
            <div key={idx} className="bg-white p-4 rounded-xl shadow-sm text-center border border-blue-50">
              <span className="font-bold text-blue-800 text-sm">{item}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-blue-700 italic">
          These procedures help treat dental infections, impacted teeth, jaw abnormalities, facial trauma, cysts, tumors, and other oral health conditions.
        </p>
      </section>

      {/* Procedures Grid */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Surgical Procedures We Perform</h2>
        <div className="grid md:grid-cols-2 gap-6">
          
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <h4 className="font-bold text-blue-700 mb-2 flex items-center gap-2">
              <Scissors className="w-5 h-5" /> Tooth Extractions
            </h4>
            <p className="text-sm text-gray-600">
              Performed when a tooth is severely damaged due to decay, trauma, or gum disease and cannot be saved. Both simple and surgical extractions are done with maximum comfort.
            </p>
          </div>

          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <h4 className="font-bold text-blue-700 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Impacted Wisdom Teeth
            </h4>
            <p className="text-sm text-gray-600">
              Surgical removal of teeth that fail to erupt properly, preventing pain, infection, and damage to neighboring teeth.
            </p>
          </div>

          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <h4 className="font-bold text-blue-700 mb-2 flex items-center gap-2">
              <Layers className="w-5 h-5" /> Jaw Fracture Management
            </h4>
            <p className="text-sm text-gray-600">
              Specialized treatment for facial injuries involving stabilization of fractured bones using plates and screws to restore proper alignment.
            </p>
          </div>

          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <h4 className="font-bold text-blue-700 mb-2 flex items-center gap-2">
              <Activity className="w-5 h-5" /> Oral Cysts & Infections
            </h4>
            <p className="text-sm text-gray-600">
              Surgical treatment to remove jaw cysts, bone infections, and oral pathological lesions to prevent further complications.
            </p>
          </div>
        </div>
      </section>

      {/* Orthognathic Surgery Highlight */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-12 shadow-xl">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <h2 className="text-3xl font-bold mb-4 !text-white">Orthognathic Surgery</h2>
            <h4 className="!text-blue-300 font-semibold mb-4">(Jaw Correction Surgery)</h4>
            <p className="!text-blue-50 leading-relaxed mb-6">
              Performed to correct protruded jaws, abnormal jaw growth, and severe misalignment. It improves facial balance, chewing efficiency, and overall aesthetics.
            </p>
            <div className="grid grid-cols-2 gap-4 text-xs font-bold uppercase tracking-widest">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-blue-400"/> Protruded Jaws</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-blue-400"/> Facial Asymmetry</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-blue-400"/> Severe Overbite</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-blue-400"/> Chewing Issues</span>
            </div>
          </div>
          <div className="bg-white/10 p-6 rounded-2xl border border-white/20 text-center">
            <p className="text-sm italic !text-blue-50">"Achieve optimal facial harmony through a combination of orthodontics and surgical jaw correction."</p>
          </div>
        </div>
      </section>

      {/* Infrastructure Section */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Advanced Surgical Infrastructure</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center p-6">
            <Microscope className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h5 className="font-bold mb-2">Modern Operation Theatre</h5>
            <p className="text-xs text-gray-500">Fully equipped and sophisticated theatre maintaining highest sterility standards.</p>
          </div>
          <div className="text-center p-6">
            <Scan className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h5 className="font-bold mb-2">3D CBCT Imaging</h5>
            <p className="text-xs text-gray-500">Detailed diagnostic evaluation for precise surgical planning and predictability.</p>
          </div>
          <div className="text-center p-6">
            <ShieldCheck className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h5 className="font-bold mb-2">Expert Surgeons</h5>
            <p className="text-xs text-gray-500">Highly skilled specialists with extensive clinical expertise in complex oral surgeries.</p>
          </div>
        </div>
      </section>

      {/* Minor Procedures Footer */}
      <section className="border-t pt-12">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Other Surgical Services</h2>
        <p className="text-gray-600 text-sm mb-6">
          At Dr. Sridhar International Dental Hospitals, we offer a range of advanced surgical procedures
designed to restore oral health, improve comfort, and prepare your mouth for successful dental
treatments.
        </p>
        <p className="text-gray-600 text-sm mb-6">
          Our experienced surgeons perform procedures such as pre-prosthetic surgery to prepare the mouth
for dentures or implants, biopsy of oral lesions for accurate diagnosis, and surgical exposure of
teeth to support orthodontic treatment.
        </p>
        <p className="text-gray-600 text-sm mb-6">
          With modern technology and expert care, we ensure safe, precise, and comfortable treatment for
every patient.
        </p>
        <div className="flex flex-wrap gap-3">
          {["Biopsy", "Pre-Prosthetic Surgery", "Lesion Removal", "Bone Grafting"].map(tag => (
            <span key={tag} className="px-4 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full">#{tag}</span>
          ))}
        </div>
      </section>
    </ServiceLayout>
  );
};

export default OralSurgery;